import { useEffect, useState } from "react";
import { getDatabaseInfo, DatabaseInfo } from "../../api/admin";

const AUTH_LABELS: Record<DatabaseInfo["authMode"], string> = {
  "connection-string": "Connection string",
  "managed-identity": "Entra ID (managed identity / az login)",
  unconfigured: "Not configured"
};

export default function DatabaseInfoPage() {
  const [info, setInfo] = useState<DatabaseInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    getDatabaseInfo()
      .then(setInfo)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load database info"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <>
      <div className="admin-page-head">
        <h1>Database</h1>
        <button type="button" className="btn-secondary btn-sm" disabled={loading} onClick={load}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      <p className="hint">
        Connection configuration and live status for the Azure SQL database backing this form. Credentials are never shown.
      </p>

      {error && <div className="banner-error">{error}</div>}

      {loading && !info ? (
        <p className="hint">Loading…</p>
      ) : info ? (
        <>
          <div style={{ margin: "1rem 0" }}>
            <span className={"status-pill " + (info.connected ? "status-ok" : "status-bad")}>
              {info.connected ? "● Connected" : "● Not connected"}
            </span>
          </div>

          {info.error && <div className="banner-error">{info.error}</div>}

          <table className="admin-table">
            <tbody>
              <tr>
                <th style={{ width: "220px", textAlign: "left" }}>Server</th>
                <td>{info.server ?? <span className="hint">— (via connection string)</span>}</td>
              </tr>
              <tr>
                <th style={{ textAlign: "left" }}>Database</th>
                <td>{info.database}</td>
              </tr>
              <tr>
                <th style={{ textAlign: "left" }}>Schema</th>
                <td>{info.schema}</td>
              </tr>
              <tr>
                <th style={{ textAlign: "left" }}>Authentication</th>
                <td>{AUTH_LABELS[info.authMode]}</td>
              </tr>
              <tr>
                <th style={{ textAlign: "left" }}>Server version</th>
                <td>{info.serverVersion ?? <span className="hint">—</span>}</td>
              </tr>
            </tbody>
          </table>

          <h2 style={{ marginTop: "1.5rem" }}>Record counts</h2>
          {info.counts ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Table</th>
                  <th>Rows</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Submissions</td>
                  <td>{info.counts.submissions.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Drafts</td>
                  <td>{info.counts.drafts.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>Settings</td>
                  <td>{info.counts.settings.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="hint">Counts unavailable — no live connection.</p>
          )}
        </>
      ) : null}
    </>
  );
}
